import {
  CancelOutlined,
  CheckCircleOutline,
  ErrorOutline,
  HourglassEmpty,
  PlayCircleOutline,
  RadioButtonUnchecked,
  ScheduleOutlined,
} from '@mui/icons-material'
import type { ChipProps } from '@mui/material'
import type { TFunction } from 'i18next'
import type { DateTime } from 'luxon'
import type { ReactElement } from 'react'

import type { AggregatePhase } from '@/features/prequalification/types'

const DATE_FORMAT = 'dd LLL yyyy, HH:mm'

export type PhaseChipProps = {
  label: string
  color: ChipProps['color']
  icon: ReactElement
  /** CANCELLED is shown with the muted "inactive" treatment rather than a colour. */
  inactive: boolean
}

/**
 * Single source of truth for how a PQ phase reads as a chip — colour, icon and
 * label. Shared by `PqPhaseChip` and the market accordion's status chip so the
 * two never drift.
 */
export function getPhaseChipProps(phase: AggregatePhase, t: TFunction): PhaseChipProps {
  switch (phase) {
    case 'NOT_STARTED':
      return {
        label: t('prequalifications.phase.not_started'),
        color: 'default',
        icon: <RadioButtonUnchecked />,
        inactive: true,
      }
    case 'SCHEDULED':
      return {
        label: t('prequalifications.phase.scheduled'),
        color: 'primary',
        icon: <ScheduleOutlined />,
        inactive: false,
      }
    case 'ENQUEUED':
      return { label: t('prequalifications.phase.enqueued'), color: 'info', icon: <HourglassEmpty />, inactive: false }
    case 'STARTED':
      return {
        label: t('prequalifications.phase.started'),
        color: 'warning',
        icon: <PlayCircleOutline />,
        inactive: false,
      }
    case 'PASSED':
      return {
        label: t('prequalifications.phase.passed'),
        color: 'success',
        icon: <CheckCircleOutline />,
        inactive: false,
      }
    case 'FAILED':
      return { label: t('prequalifications.phase.failed'), color: 'error', icon: <ErrorOutline />, inactive: false }
    case 'CANCELLED':
      return {
        label: t('prequalifications.phase.cancelled'),
        color: 'default',
        icon: <CancelOutlined />,
        inactive: true,
      }
  }
}

/**
 * Extra tooltip context for a phase chip, or `undefined` when the label already says
 * everything.
 *
 * Only `SCHEDULED` needs it: on its own the chip can't tell a run starting in five
 * minutes from one starting next week, and the run's `startAt` is the only place that
 * distinction lives. Every other phase is fully described by its label, so those chips
 * stay untooltipped rather than repeating it on hover.
 */
export function phaseChipTooltip(phase: AggregatePhase, startAt: DateTime | null, t: TFunction): string | undefined {
  if (phase !== 'SCHEDULED' || !startAt) return undefined

  return t('prequalifications.phase.scheduled_for', { time: startAt.toFormat(DATE_FORMAT) })
}
